// ===========================================
// HOOK: useLocalStorage
// ===========================================

import { useState, useCallback, useEffect } from 'react'

/**
 * Hook para persistir estado en localStorage
 *
 * @example
 * const [theme, setTheme] = useLocalStorage('theme', 'light')
 */
export function useLocalStorage<T>(
  key: string,
  initialValue: T
): [T, (value: T | ((prev: T) => T)) => void] {
  // Obtener valor inicial
  const [storedValue, setStoredValue] = useState<T>(() => {
    if (typeof window === 'undefined') {
      return initialValue
    }

    try {
      const item = window.localStorage.getItem(key)
      return item ? JSON.parse(item) : initialValue
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error)
      return initialValue
    }
  })

  // Setter que tambien guarda en localStorage
  const setValue = useCallback(
    (value: T | ((prev: T) => T)) => {
      try {
        // Permitir funcion como en useState
        const valueToStore = value instanceof Function ? value(storedValue) : value
        setStoredValue(valueToStore)

        if (typeof window !== 'undefined') {
          window.localStorage.setItem(key, JSON.stringify(valueToStore))
        }
      } catch (error) {
        console.warn(`Error setting localStorage key "${key}":`, error)
      }
    },
    [key, storedValue]
  )

  // Releer cuando cambia la clave. Sin esto el valor inicial queda congelado desde el mount, y una
  // clave que se completa después —el id del usuario, que la sesión resuelve en un render posterior—
  // devolvía el default en vez de la preferencia guardada de esa persona.
  useEffect(() => {
    if (typeof window === 'undefined') return
    try {
      const item = window.localStorage.getItem(key)
      setStoredValue(item ? JSON.parse(item) : initialValue)
    } catch (error) {
      console.warn(`Error reading localStorage key "${key}":`, error)
    }
    // `initialValue` queda fuera a propósito: un literal recreado en cada render reiniciaría el
    // valor a mano en cada pasada.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key])

  // Sincronizar entre tabs
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === key && e.newValue !== null) {
        try {
          setStoredValue(JSON.parse(e.newValue))
        } catch {
          // Ignorar errores de parse
        }
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [key])

  return [storedValue, setValue]
}
